/* eslint-disable prefer-const */
/* eslint-disable react/no-array-index-key */
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from 'styled-components';

import { SPACING } from '~/common/layout-propertis/spacing';

import {
  Container,
  OptionsContainer,
  OptionWrapper,
  RightIconWrapper,
  SelectedBorder,
} from './styles';
import { Option } from './Option';
import { ScrollOverlay } from '../../ScrollOverlay';
import { Icon } from '../../Icons';
import { ITabOption } from '../../../interfaces/ITabOption';

export interface IHorizontalTabOption extends ITabOption {
  /** é obrigatório esse elemento ter 20px de altura e largura! */
  rightContent?: ReactNode;
}

interface HorizontalTabMenuProps {
  /** array de opções do tabmenu */
  options?: IHorizontalTabOption[];
  /** state selecionado no tab */
  current: string;
  /** condição para aplicar largura mínima no menu (se não irá ter largura automática) */
  minWidth?: boolean;
  /** condição para exibir as setas entre as opções */
  withArrows?: boolean;
  /** condição para exibir a borda inferior do menu */
  withUnderlineBorder?: boolean;
  /** condição para destacar a cor do título da opção selecionada */
  highlighTitle?: boolean;
  /** condição para destacar a cor do ícone da opção selecionada */
  highlighIcon?: boolean;
  /** condição para renderizar com a total largura livre e aplicar espaçamento automático */
  maxWidthAndSpaceBetween?: boolean;
  /** tipo de espaçamento entre as opções */
  justifyOptions?: 'start' | 'space-between';
  /** gap das opções */
  optionsGap?: SPACING;
  /** condição para aplicar largura máxima no menu (se não irá ter largura automática) */
  fullWidth?: boolean;
  /** condição para aplicar scroll através de botões */
  withScrollButton?: boolean;
  /** condição para exibir a borda destacada na opção selecionada */
  withSelectedUnderlineBorderHighligh?: boolean;
  /** ícone no fim das opções à direita */
  rightIcon?: string;
  /** propriedade para fixar a largura das opções */
  fixedOptionWidth?: string;
  /** condição para não exibir overflow-x */
  withoutOverflowX?: boolean;
  /** função de alteração que passa o key selecionado para o tabmenu */
  onChange: (key: string) => void;
}

export const Horizontal: FC<HorizontalTabMenuProps> = ({
  options = [],
  current,
  minWidth = false,
  withArrows = false,
  withUnderlineBorder = false,
  highlighTitle = true,
  highlighIcon = true,
  withScrollButton = false,
  withoutOverflowX = false,
  maxWidthAndSpaceBetween = false,
  justifyOptions = 'start',
  optionsGap = SPACING._24,
  fullWidth = false,
  withSelectedUnderlineBorderHighligh = true,
  fixedOptionWidth,
  rightIcon,
  onChange,
}) => {
  const ulRef = useRef<HTMLUListElement>(null);
  const theme = useTheme();
  const [render, setRender] = useState<boolean>(false);
  const selectedTabDynamicProps = useMemo(() => {
    if (!render || !withSelectedUnderlineBorderHighligh) {
      return { width: '0px', left: '0px' };
    }

    const selectedElement = document?.getElementById?.(`tab-option-${current}`);
    const option = options.find((where) => where.key === current);
    if (!selectedElement || !render || !option) {
      return { width: '0px', left: '0px' };
    }

    let { offsetWidth, offsetLeft } = selectedElement;

    if (option.icon || option.rightContent || withArrows) {
      let toSum = 0;
      const x = options.findIndex((where) => where.key === current);

      if (option.icon) {
        toSum += 28;
      }

      if (withArrows) {
        toSum += 28;
      }

      if (withArrows && x === options.length - 1) {
        offsetWidth -= 28;
      }

      return {
        width: `${offsetWidth + toSum}px`,
        left: `${offsetLeft + toSum * x}px`,
      };
    }

    return {
      width: `${offsetWidth}px`,
      left: `${offsetLeft}px`,
    };
  }, [
    current,
    render,
    options,
    withArrows,
    withSelectedUnderlineBorderHighligh,
  ]);

  useEffect(() => {
    setRender(true);
  }, []);

  return (
    <Container withButtonScroll={withScrollButton} minWidth={minWidth}>
      <OptionsContainer
        ref={ulRef}
        justifyOptions={justifyOptions}
        optionsGap={optionsGap}
        withUnderlineBorder={withUnderlineBorder}
        fullWidth={fullWidth}
        withButtonScroll={withScrollButton}
        withoutOverflowX={withoutOverflowX}
        maxWidthAndSpaceBetween={maxWidthAndSpaceBetween}
      >
        {options.map((option, index) => (
          <OptionWrapper key={option.key} id={`tab-option-${option.key}`}>
            <Option
              customIcon={option.customIcon}
              option={option}
              current={current}
              highlighIcon={highlighIcon}
              highlighTitle={highlighTitle}
              optionsLength={options.length}
              withArrows={withArrows}
              index={index}
              key={String(index)}
              width={fixedOptionWidth}
              onChange={onChange}
            />
          </OptionWrapper>
        ))}

        {withSelectedUnderlineBorderHighligh && (
          <SelectedBorder
            id="selected-border"
            style={{ ...selectedTabDynamicProps }}
          />
        )}

        {rightIcon && (
          <RightIconWrapper>
            <Icon type={rightIcon} color={theme.neutral[400]} size={20} />
          </RightIconWrapper>
        )}
      </OptionsContainer>

      {withScrollButton && <ScrollOverlay ulRef={ulRef} />}
    </Container>
  );
};
